import { useContext, useEffect, useState } from "react"

import "./Users.css"
import DeleteModal from "../../Modals/DeleteModal"
import User from "../../models/User"
import { Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClose, faEye, faTrash } from "@fortawesome/free-solid-svg-icons"
import useFetch from "../../../hooks/use-fetch"
import { toast } from "react-toastify"
import { AuthContext } from "../../../context/AuthProvider"
import { useNavigate } from "react-router-dom"


interface createUserProps {
    reloadUsers: () => void
    setIsCreating: (newValue: boolean) => void
}

const CreateUser: React.FC<createUserProps> = ({reloadUsers, setIsCreating}) => {
    const { post } = useFetch()
    const [email, setEmail] = useState<string | null>(null)
    const [firstName, setFirstName] = useState<string | null>(null)
    const [lastName, setLastName] = useState<string | null>(null)
    const [isAdmin, setIsAdmin] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>('')

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await post('/users/', {email, firstName, lastName, isAdmin})
            setIsCreating(false)
            toast.success('Utente creato con successo')
            reloadUsers()
        } catch(err: any){
            if (err?.status === 400){
                toast.error('L\'indirizzo email è già registrato')
            } else {
                toast.error('Errore durante la creazione dell\'utente ')
            }
        }
    }

    return (    
          <div className="box users-box" > 
            <FontAwesomeIcon
                icon={faClose}
                className="action-icon delete close"
                onClick={() => {setIsCreating(false)}}
            />
            <h2 className="text-xl mb-5 text-gray-800">Nuovo utente</h2>
            <form onSubmit={handleRegister}>
                <div className="textbox">
                <label htmlFor="email">Email:</label>
                <input
                    type="email"
                    id="email"
                    value={email ? email : ''}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    required
                    onInvalid={(e) => {
                        e.preventDefault()
                        setErrorMessage('Inserisci un indirizzo email valido.');
                    }}
                />
                </div>

                <div className="textbox">
                    <label htmlFor="firstName">Nome:</label>
                    <input
                        type="text"
                        id="firstName"
                        value={firstName ? firstName : ''}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Nome"
                        required
                    />
                </div>
                <div className="textbox">
                    <label htmlFor="lastName">Cognome:</label>
                    <input
                        type="text"
                        id="lastName"
                        value={lastName ? lastName : ''}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Cognome"
                        required
                    />
                </div>
                <div>
                    <Checkbox checked={isAdmin} onClick={() => setIsAdmin(!isAdmin)}/><label htmlFor="admin">Admin</label>
                </div>
                {errorMessage && <p className="error-message">{errorMessage}</p>}
                <button className="btn primary">Salva</button>
            </form>
        </div>
    )
}

type Order = "asc" | "desc";

const Users = () => {
    const { get, remove, update } = useFetch()
    const navigate = useNavigate()
    const { user } = useContext(AuthContext)

    const [users, setUsers] = useState<User[]>([])
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [orderBy, setOrderBy] = useState<keyof User>("email");
    const [order, setOrder] = useState<Order>("asc");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
    const [userToDelete, setUserToDelete] = useState<User | null>(null)
    const [isCreating, setIsCreating] = useState<boolean>(false)
    

    useEffect(() => {
        fetchUsers()
    }, [])


    useEffect(() => {
        const lower = searchTerm.toLowerCase()
        const updated = users.filter((u) =>
            u?.email?.toLowerCase().includes(lower) ||
            u?.firstName?.toLowerCase().includes(lower) ||
            u?.lastName?.toLowerCase().includes(lower)
        )
        setFilteredUsers(updated)
    }, [searchTerm, users])


    const fetchUsers = async () => {
        try{
            const data = await get('/users/')
            setUsers(data.users)
            setFilteredUsers(data.users)
        } catch(err){
            console.log('Error fetching users')
        }
    }

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };
    
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSort = (property: keyof User) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);

        const sorted = [...filteredUsers].sort((a, b) => {
            if (a[property]! < b[property]!) return isAsc ? -1 : 1;
            if (a[property]! > b[property]!) return isAsc ? 1 : -1;
            return 0;
        });
        setFilteredUsers(sorted);
    };

    const handleDelete = async () => {
        try{
            await remove(`/users/${userToDelete?.id}`)
            setIsDeleteModalOpen(false)
            fetchUsers()
            toast.success('Utente eliminato con successo')
        } catch(err){
            toast.error("Errore durante l'eliminazione dell'utente")
            setIsDeleteModalOpen(false)
        }
    }

    const handleEdit = async (clickedUser: User) => {
        clickedUser.isAdmin = !clickedUser.isAdmin
        try{
            await update(`/users/${clickedUser.id}`, clickedUser )
            setIsDeleteModalOpen(false)
            fetchUsers()
        } catch(err){
            toast.error("Errore durante la modifica dell'utente")
            clickedUser.isAdmin = !clickedUser.isAdmin
        }
    }


    return (
        <div className="main-container">
            { isCreating && <CreateUser reloadUsers={fetchUsers} setIsCreating={setIsCreating}/>}
            {/* <button 
                className="btn primary"
                style={{alignSelf: 'flex-end'}} 
                onClick={() => setIsCreating(true)}
            >Nuovo Utente</button> */}

            {isDeleteModalOpen &&
                <DeleteModal
                    title={'Elimina'} 
                    description={
                    <>
                        <p>Sei sicuro di voler eliminare l'utente?</p>
                        <p>
                            <strong>{userToDelete?.email}</strong> 
                        </p>
                    </>
                    }
                    onConfirm={handleDelete}
                    onCancel={()=>setIsDeleteModalOpen(false)}
                />
            }
            <div style={{margin: '25px 0', width: '100%'}}>
                <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca utente"
                className="search-input"
                />
            </div>
      
            <TableContainer 
                component={Paper} 
                sx={{ flex: 1, width: '1000pt', height: '100%', borderRadius:'16px'}}
                >
                <Table>
                <TableHead>
                    <TableRow>
                    {[
                        { id: "id", label: "ID" },
                        { id: "firstName", label: "Nome" },
                        { id: "lastName", label: "Cognome" },
                        { id: "email", label: "Email" },
                        { id: "isAdmin", label: 'Admin'}
                    ].map((col) => (
                        <TableCell key={col.id} sx={{fontSize: '14px', fontWeight: '600'}}>
                        <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : "asc"}
                            onClick={() => handleSort(col.id as keyof User)}
                        >
                            {col.label}
                        </TableSortLabel>
                        </TableCell>
                    ))}
                    <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredUsers.map((u) => (
                    <TableRow key={u.id}>
                        <TableCell>{u.id}</TableCell>
                        <TableCell>{u?.firstName}</TableCell>
                        <TableCell>{u?.lastName}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                            <Checkbox 
                                checked={u.isAdmin || u.isSuperuser} disabled={u.id === user?.id}
                                onClick={()=>handleEdit(u)}
                            />
                        </TableCell>
                        <TableCell>
                            <div className="table-actions">
                                <FontAwesomeIcon
                                    icon={faEye}
                                    className="action-icon view"
                                    onClick={() => {navigate(`/consumptions/${u.id}`)}}
                                />
                            </div>
                        </TableCell>
                        <TableCell>
                            { u.id !== user?.id &&
                            <div className="table-actions">
                                <FontAwesomeIcon
                                    icon={faTrash}
                                    className="action-icon delete"
                                    onClick={() => {setIsDeleteModalOpen(true); setUserToDelete(u)}}
                                />
                            </div>
                            }
                        </TableCell>
                    </TableRow>
                    ))}
                </TableBody>
                </Table>


                <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredUsers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        </div>
    )
}


export default Users
